import {
  Shape,
  WallPathEdge,
  WallPathShape,
  WallPathVertex
} from '../types';
import { computeWallPathBounds } from './shapeFactory';

/**
 * Auto-merge open wall-paths whose endpoints exactly coincide. Runs after
 * every user-initiated `setShapes` commit so the user can drag a wall-path's
 * endpoint onto another path's endpoint and have them stitch into a single
 * path automatically — same UX they get when drawing one continuous path.
 *
 * Rules:
 *   - Both paths must be `closed !== true`.
 *   - Endpoints compare to nearest pixel (`Math.round`) — robust to float
 *     noise from rotation/snap, but visually-distinct vertices won't merge.
 *   - When both endpoints between a pair match, the result is a closed path.
 *   - The merged path inherits style (thickness/color/dash/fill/etc.) from
 *     the first of the two in the shapes array — predictable and stable.
 *   - Re-runs to fixed point so a chain of three+ open paths sharing
 *     endpoints all collapse in one commit.
 */

const samePoint = (a: WallPathVertex, b: WallPathVertex): boolean =>
  Math.round(a.x) === Math.round(b.x) &&
  Math.round(a.y) === Math.round(b.y);

/** Reverse a single edge: when traversal direction flips, signed offsets
 *  along it (curvature, curvatureCenter, door/window position) flip too. */
function reverseEdge(edge: WallPathEdge): WallPathEdge {
  return {
    ...edge,
    curvature: edge.curvature !== undefined ? -edge.curvature : undefined,
    curvatureCenter:
      edge.curvatureCenter !== undefined ? 1 - edge.curvatureCenter : undefined,
    doors: edge.doors?.map((d) => ({ ...d, position: 1 - d.position })),
    windows: edge.windows?.map((w) => ({ ...w, position: 1 - w.position }))
  };
}

/** Reverse a wall-path: vertices in reverse order; edges in reverse order
 *  AND each edge individually reversed. Per-vertex cornerRadius is preserved
 *  (it's a property of the corner, not direction-dependent). */
function reverseWallPath(p: WallPathShape): WallPathShape {
  return {
    ...p,
    vertices: [...p.vertices].reverse(),
    edges: [...p.edges].reverse().map(reverseEdge)
  };
}

interface OrientedPair {
  /** Already oriented so `A.last == B.first`. */
  A: WallPathShape;
  B: WallPathShape;
}

/** Find a way to orient (a, b) so that A.last meets B.first, if one exists.
 *  Returns null when no endpoint pair matches. */
function orientForJoin(
  a: WallPathShape,
  b: WallPathShape
): OrientedPair | null {
  const aFirst = a.vertices[0];
  const aLast = a.vertices[a.vertices.length - 1];
  const bFirst = b.vertices[0];
  const bLast = b.vertices[b.vertices.length - 1];

  if (samePoint(aLast, bFirst)) return { A: a, B: b };
  if (samePoint(aLast, bLast)) return { A: a, B: reverseWallPath(b) };
  // For the remaining two cases we swap so style inheritance still picks
  // the first shape (via the outer caller's order) — the original `a` is
  // always treated as A, so reverse it instead of swapping the roles.
  if (samePoint(aFirst, bLast)) return { A: reverseWallPath(a), B: reverseWallPath(b) };
  if (samePoint(aFirst, bFirst)) return { A: reverseWallPath(a), B: b };
  return null;
}

function mergePair(a: WallPathShape, b: WallPathShape): WallPathShape | null {
  if (a.closed || b.closed) return null;
  const oriented = orientForJoin(a, b);
  if (!oriented) return null;
  const { A, B } = oriented;

  // After orientation A.last === B.first. If A.first === B.last too, the
  // joined chain forms a closed loop — strip the duplicate vertex at both
  // ends and mark closed.
  const aFirst = A.vertices[0];
  const bLast = B.vertices[B.vertices.length - 1];
  const closesLoop = samePoint(aFirst, bLast);

  // Open: shared vertex A.last/B.first appears once → slice(1).
  // Closed: also drop B's last (== A's first) → slice(1, -1).
  const mergedVertices: WallPathVertex[] = closesLoop
    ? [...A.vertices, ...B.vertices.slice(1, -1)]
    : [...A.vertices, ...B.vertices.slice(1)];
  const mergedEdges: WallPathEdge[] = [...A.edges, ...B.edges];

  const bounds = computeWallPathBounds(mergedVertices);
  // Inherit style from `a` (the first selected / first in the shapes array)
  // so the merge result is predictable. Preserve `a.id` so any selection
  // referring to a stays valid.
  return {
    ...a,
    vertices: mergedVertices,
    edges: mergedEdges,
    closed: closesLoop || false,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height
  };
}

/** Replace shapes[idx] with `replacement` and drop the shape at `removeIdx`.
 *  Returns a new array. */
function applyMerge(
  shapes: Shape[],
  idx: number,
  removeIdx: number,
  replacement: WallPathShape
): Shape[] {
  const next = shapes.slice();
  next[idx] = replacement;
  // Splice in the higher index first so the lower index doesn't shift.
  next.splice(removeIdx, 1);
  return next;
}

export function autoMergeWallPaths(shapes: Shape[]): Shape[] {
  let current = shapes;

  // Iterate to fixed point — chains of 3+ paths collapse in one commit.
  // Bound the loop to avoid pathological cases (shouldn't happen, but
  // belt-and-braces).
  for (let safety = 0; safety < shapes.length; safety++) {
    let merged: { iIdx: number; jIdx: number; result: WallPathShape } | null =
      null;

    for (let i = 0; i < current.length && !merged; i++) {
      const a = current[i];
      if (a.type !== 'wall-path' || a.closed) continue;
      for (let j = i + 1; j < current.length && !merged; j++) {
        const b = current[j];
        if (b.type !== 'wall-path' || b.closed) continue;
        const result = mergePair(a as WallPathShape, b as WallPathShape);
        if (result) merged = { iIdx: i, jIdx: j, result };
      }
    }

    if (!merged) break;
    current = applyMerge(current, merged.iIdx, merged.jIdx, merged.result);
  }

  return current;
}
